import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Route, useParams } from "react-router-dom";

import { byDefined } from "../../../../commons/libs/filter-guards";
import { getSpecFormatter } from "../../../../commons/libs/formatters";
import { getBrandLogoUrl } from "../../../../commons/libs/resource-paths";
import {
  getFirstVariantId,
  getSingleSpecValue,
  getStandaloneProductVariant,
  getStandaloneProductVariantById
} from "../../../../commons/libs/specs";
import { getSpecConfig } from "../../../../commons/specs";
import { Product, ProductId, ProductKey, ProductSpecKey } from "../../../../commons/specs/product";
import { Brand } from "../../../../commons/types/brand";
import * as icons from "../../../../resources/icons";
import { IconSmallCross } from "../../../../resources/icons";
import { useTrackingContext } from "../../../commons/container/utils/tracking-context";
import { buildProductPath } from "../../../commons/libs/path";
import { getNeighbouringProducts } from "../../../commons/libs/products";
import BrandImage from "../../components/BrandImage/BrandImage";
import Button from "../../components/Button/Button";
import CenteredPageLayout from "../../components/CenteredPageLayout/CenteredPageLayout";
import FlexLayout from "../../components/FlexLayout/FlexLayout";
import Icon from "../../components/Icon/Icon";
import LoadingIndicator from "../../components/LoadingIndicator/LoadingIndicator";
import MainContentLayout from "../../components/MainContentLayout/MainContentLayout";
import ProductDetailsContentLayout from "../../components/ProductDetailsContentLayout/ProductDetailsContentLayout";
import SwipeableContent, { type TransitionDirection } from "../../components/SwipeableContent/SwipeableContent";
import TabBarPageLayout from "../../components/TabBarPageLayout/TabBarPageLayout";
import { useAppHistory, useAppLocation } from "../../libs/hooks/use-app-routing";
import { useBrands } from "../../libs/queries/use-brands";
import useProduct from "../../libs/queries/use-product";
import { useSettings } from "../../libs/queries/use-settings";
import useVeloconnectEndpoint, { UseVeloconnectEndpointReturn } from "../../libs/queries/use-veloconnect-endpoint";
import { useWawiConnectionInfo } from "../../libs/queries/use-wawi-connection";
import { ROUTES } from "../../routes";
import { useCommonAppDispatcher } from "../../store/hooks";
import ProductTitlePartial from "../partials/ProductTitlePartial";
import { VeloconnectEndpointProvider } from "../utils/veloconnect-endpoint-context";

import ProductDetailsImagePartial from "./ProductDetailsImagePartial";

export interface ProductDetailsPartialRenderProps {
  productId: ProductId;
  variantId: ProductId | undefined;
  product: Product | undefined;
  productContent?: {
    key: string;
    link: string;
  };
  productInitializingFirstTime: boolean;
  brand: Brand | undefined;
  veloconnectEndpoint: UseVeloconnectEndpointReturn;
  isAsideAnimationRunning: boolean;
}

interface Props {
  tabBar: (props: ProductDetailsPartialRenderProps) => React.ReactNode;
  tabBarOverlay: (props: ProductDetailsPartialRenderProps) => React.ReactNode;
  advertisement?: (props: ProductDetailsPartialRenderProps) => React.ReactNode;
  productContentAside?: (props: ProductDetailsPartialRenderProps) => React.ReactNode;
  productContentCta?: (props: ProductDetailsPartialRenderProps) => React.ReactNode;
  backButton: () => React.ReactNode;
  onProductLoad?: (product: Product) => void;
}

const ProductDetailsPartial = ({
  tabBar,
  backButton,
  tabBarOverlay,
  advertisement,
  productContentAside,
  productContentCta,
  onProductLoad
}: Props) => {
  const { t, i18n } = useTranslation(["commons"]);
  const history = useAppHistory();
  const location = useAppLocation();
  const { id: productId } = useParams<{ id: ProductId }>();

  const [dispatch, actions] = useCommonAppDispatcher();
  const settings = useSettings();
  const brands = useBrands();
  // The WaWi connection can change outside of the app, so refresh it whenever product details are entered.
  useWawiConnectionInfo({ refetchOnMount: "always" });
  const { mixpanel } = useTrackingContext();

  const productListContext = (location.state || {}).productListContext || [];
  const variantId = location.state?.variantId;

  const [transitionDirection, setTransitionDirection] = React.useState<TransitionDirection>("next");
  const [isAsideAnimationRunning, setIsAsideAnimationRunning] = React.useState(false);

  const product = useProduct({
    productId,
    options: {
      onError: error => dispatch(actions.error.set(error)),
      onData: product => {
        if (product) {
          onProductLoad?.(product);
        }
      }
    }
  });

  const productVariant = useMemo(() => {
    if (!product.query.data) {
      return undefined;
    }

    const actualVariantId = variantId ?? getFirstVariantId(product.query.data);

    return (
      getStandaloneProductVariantById(product.query.data, actualVariantId) ??
      getStandaloneProductVariant(product.query.data)
    );
  }, [product.query.data, variantId]);

  const specFormatter = useMemo(() => {
    if (!product.query.data) {
      return undefined;
    }

    const productType = product.query.data[ProductKey.ProductType];
    const specConfig = getSpecConfig(productType);

    return getSpecFormatter(productType, specConfig.specDefinitions, i18n, settings.initialized.customization);
  }, [product.query.data, i18n, settings.initialized.customization]);

  const tabBarRoutes = useMemo(
    () =>
      [
        ROUTES.PRODUCT_DETAILS.SPECIFICATIONS,
        ROUTES.PRODUCT_DETAILS.BOOKMARK,
        ROUTES.PRODUCT_DETAILS.BOOKMARK_COMPARE,
        ROUTES.PRODUCT_DETAILS.AVAILABILITY
      ].map(route => buildProductPath(route, { productId })),
    [productId]
  );

  const isOnTabBarRoute = location.pathname ? tabBarRoutes.includes(location.pathname) : false;

  const productContentKey = product.query.data
    ? getSingleSpecValue(ProductSpecKey.ProductContentKey, product.query.data)
    : undefined;
  const productContentLink = productContentKey
    ? buildProductPath(ROUTES.PRODUCT_DETAILS.PRODUCT_CONTENT, {
        productId,
        productContentKey
      })
    : undefined;

  const isOnProductContentRoute = location.pathname === productContentLink;

  const productInitializingFirstTime = !product.query.data && product.query.isFetching;

  const brandKey = product.query.data && getSingleSpecValue(ProductSpecKey.BrandKey, product.query.data);
  const brand = brandKey ? brands.getBrand(brandKey) : undefined;
  const neighbours = getNeighbouringProducts(productListContext, productId);

  const veloconnectEndpoint = useVeloconnectEndpoint(brandKey);

  const renderProps: ProductDetailsPartialRenderProps = {
    productId,
    variantId,
    product: product.query.data,
    brand,
    productInitializingFirstTime,
    veloconnectEndpoint,
    isAsideAnimationRunning,
    ...(productContentKey && productContentLink
      ? { productContent: { key: productContentKey, link: productContentLink } }
      : undefined)
  };

  React.useEffect(() => {
    if (product.query.data) {
      mixpanel?.trackProductViewed(product.query.data);
    }

    // Track only when product id changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.query.data?.[ProductKey.ProductId]]);

  const handleNextProduct = (interaction: "click" | "swipe") => {
    if (!product.query.isFetching && brand && product.query.data && neighbours.next != null) {
      mixpanel?.trackProductDetailsNavigated(
        interaction,
        "next",
        { productId, brandKey: brand.key, modelName: getSingleSpecValue(ProductSpecKey.ModelName, product.query.data) },
        neighbours.next.productId
      );

      setTransitionDirection("next");
      history.replace(
        buildProductPath(ROUTES.PRODUCT_DETAILS.INDEX, { productId: String(neighbours.next.productId) }),
        {
          ...location.state,
          variantId: neighbours.next.variantId
        }
      );
    }
  };

  const handlePrevProduct = (interaction: "click" | "swipe") => {
    if (!product.query.isFetching && brand && product.query.data && neighbours.prev != null) {
      mixpanel?.trackProductDetailsNavigated(
        interaction,
        "prev",
        { productId, brandKey: brand.key, modelName: getSingleSpecValue(ProductSpecKey.ModelName, product.query.data) },
        neighbours.prev.productId
      );

      setTransitionDirection("prev");
      history.replace(
        buildProductPath(ROUTES.PRODUCT_DETAILS.INDEX, { productId: String(neighbours.prev.productId) }),
        {
          ...location.state,
          variantId: neighbours.prev.variantId
        }
      );
    }
  };

  return (
    <Route
      exact
      path={ROUTES.PRODUCT_DETAILS.INDEX}
      children={() => (
        <VeloconnectEndpointProvider endpoint={veloconnectEndpoint}>
          <TabBarPageLayout
            isTabBarOverlayOpen={!productInitializingFirstTime && isOnTabBarRoute}
            tabBar={tabBar(renderProps)}
            tabBarOverlay={tabBarOverlay(renderProps)}
            onCloseTabBarOverlay={() => {
              history.replace(buildProductPath(ROUTES.PRODUCT_DETAILS.INDEX, { productId }), location.state);
            }}
            isAsideOpen={isOnProductContentRoute}
            onAsideAnimationStarted={() => setIsAsideAnimationRunning(true)}
            onAsideAnimationCompleted={() => setIsAsideAnimationRunning(false)}
            aside={productContentAside?.(renderProps)}
            asideCloseButton={
              <Button
                onClick={() => history.goBack()}
                variant="accent"
                kind="solid"
                icon={<Icon source={IconSmallCross} />}
              >
                {t("productDetailsPartialProductDetails.closeAsideButtonLabel")}
              </Button>
            }
            asideProductTitle={
              <ProductTitlePartial
                product={product.query.data}
                wrap
                align="left"
                layout="vertical"
                customization={settings.initialized.customization}
                currency={settings.initialized.currency}
                assortmentPriceSettings={settings.assortmentPriceSettings}
              />
            }
          >
            <ProductDetailsContentLayout
              itemsRight={[productContentCta?.(renderProps), advertisement?.(renderProps)].filter(byDefined)}
              alignment={!!advertisement?.(renderProps) ? "right-bottom" : "right-center"}
              isAsideOpen={isOnProductContentRoute}
            >
              {!product.query.data && (
                <CenteredPageLayout>
                  <LoadingIndicator size="s" />
                </CenteredPageLayout>
              )}
              {product.query.data && productVariant && specFormatter && (
                <MainContentLayout
                  layout="full"
                  headerLeft={backButton()}
                  // TODO: Refactor with header component. Adding a simple margin works fine but just in this special case. It breaks out of the header
                  headerRight={
                    brand && (
                      <FlexLayout alignItems="center" gap="sl" classNames={["u-space-top-l"]}>
                        {brand?.highlighted ? <Icon source={icons.IconMediumHighlight} size="l" /> : undefined}
                        <BrandImage position="right" src={getBrandLogoUrl(brand.key)} />
                      </FlexLayout>
                    )
                  }
                  backgroundImage={null}
                  hideHeaderBackground
                >
                  <SwipeableContent
                    transitionDirection={transitionDirection}
                    classNames={["u-space-top-s", "u-space-xl"]}
                    slideKey={product.query.data[ProductKey.ProductId]}
                    onPrev={() => handlePrevProduct("swipe")}
                    onNext={() => handleNextProduct("swipe")}
                  >
                    <ProductDetailsImagePartial
                      isProductContentAsideOpen={isOnProductContentRoute}
                      product={product.query.data}
                      productVariant={productVariant}
                    />
                  </SwipeableContent>
                </MainContentLayout>
              )}
            </ProductDetailsContentLayout>
          </TabBarPageLayout>
        </VeloconnectEndpointProvider>
      )}
    />
  );
};

export default ProductDetailsPartial;
